import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import clsx from 'clsx';
import { PlaybackTimeline } from './PlaybackTimeline';
import { camerasApi } from '@api/cameras';
import { HttpError } from '@api/http';
import { recordingsApi } from '@api/recordings';
import { streamsApi } from '@api/streams';
import { useTimeline } from '@hooks/useTimeline';
import { formatLocalYmd, getViewerUtcOffsetMinutes } from '@lib/date-ymd';
import { dbgPlayback } from '@lib/dbg-playback';
import {
  getSegmentPlaybackOffsetSeconds,
  offsetForWallClockMapping,
  playlistSeekFromWallClockMs,
  resolveTimelineSeekTargetSeconds,
  sortSegmentsChronological,
  wallClockMsFromPlaylistOffset,
} from '@lib/timeline-playback';
import {
  resetVideoElement,
  startHlsPlayback,
  type HlsPlaybackSession,
} from '@lib/hls-player';
import type { Camera, ManualRecordingStatus, TimelineSegment } from '@api/types';
import { useHasPermission } from '@auth/guards';

interface Props {
  camera: Camera;
}

type PlayerPhase = 'loading' | 'playing' | 'offline' | 'error';

/** Recording-only nudge for rewind / fast-forward (matches timeline tooltips). */
const SEEK_STEP_SECONDS = 10;

export function CameraPlaybackPanel({ camera }: Props): JSX.Element {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const pendingPlaylistSeek = useRef<number | null>(null);
  /** When HLS `duration` grows after manifest, re-apply a deep playlist seek once it fits. */
  const timelineSeekCatchupRef = useRef<(() => void) | null>(null);
  /** Set true in playback `useEffect` cleanup so duration catch-up does not run after unmount / mode switch. */
  const playbackCancelledRef = useRef(false);
  const previousTimelineDateRef = useRef<string>('');

  const [cameraNavIds, setCameraNavIds] = useState<number[]>([]);

  const canRecordingView = useHasPermission('recording_view');
  const { data, date, setDate, isLoading, error, refresh } = useTimeline(camera.id, canRecordingView);
  const [playbackWallClockMs, setPlaybackWallClockMs] = useState<number | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<TimelineSegment | null>(null);
  const [phase, setPhase] = useState<PlayerPhase>('loading');
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [muted, setMuted] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [headerTick, setHeaderTick] = useState(0);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportHint, setExportHint] = useState<string | null>(null);
  /** After user clicks Export, Shift+drag and range UI are enabled until export completes or they cancel. */
  const [exportPickingActive, setExportPickingActive] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [manualRecording, setManualRecording] = useState<ManualRecordingStatus>({
    active: camera.manualRecordingStartedAt != null,
    startedAt: camera.manualRecordingStartedAt,
  });
  const [manualRecordingBusy, setManualRecordingBusy] = useState(false);
  /** Avoid re-seeding wall-clock on timeline `data` polls; only seed when the selected segment id actually changes. */
  const lastSeededSegmentIdForPlaybackRef = useRef<string | null>(null);
  /** Wall-clock range (viewer day axis) for clip export; set by Shift+drag on timeline or by selecting a segment. */
  const [exportRangeWallMs, setExportRangeWallMs] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const canRecordingControl = useHasPermission('recording_control');
  const canExportSnapshot = useHasPermission('export_snapshot');

  const isLive = selectedSegment === null;
  const todayIso = useMemo(() => {
    void headerTick;
    return formatLocalYmd(new Date());
  }, [headerTick]);
  const showJumpToToday = date !== todayIso;

  const timelineBusyMessage = useMemo(() => {
    if (!canRecordingView) return null;
    if (isLoading) return 'Loading timeline…';
    if (!isLive && phase === 'loading') return 'Preparing playback…';
    if (!isLive && phase === 'playing' && mediaBusy) return 'Buffering…';
    return null;
  }, [canRecordingView, isLoading, isLive, phase, mediaBusy]);

  const showPlaybackOverlay = useMemo(() => {
    if (isLive) return phase !== 'playing';
    return isLoading || phase !== 'playing' || (phase === 'playing' && mediaBusy);
  }, [isLive, isLoading, phase, mediaBusy]);

  useEffect(() => {
    const controller = new AbortController();
    camerasApi
      .list({ page: 1, pageSize: 100 }, controller.signal)
      .then((res) => {
        const ids = [...res.items].sort((a, b) => a.id - b.id).map((c) => c.id);
        setCameraNavIds(ids);
      })
      .catch(() => {
        if (!controller.signal.aborted) setCameraNavIds([]);
      });
    return () => controller.abort();
  }, []);

  const { previousCameraId, nextCameraId } = useMemo(() => {
    const idx = cameraNavIds.indexOf(camera.id);
    if (idx < 0) {
      return { previousCameraId: null as number | null, nextCameraId: null as number | null };
    }
    return {
      previousCameraId: idx > 0 ? cameraNavIds[idx - 1]! : null,
      nextCameraId: idx < cameraNavIds.length - 1 ? cameraNavIds[idx + 1]! : null,
    };
  }, [cameraNavIds, camera.id]);

  const goPreviousCamera = useCallback(() => {
    if (previousCameraId != null) navigate(`/cameras/${previousCameraId}/playback`);
  }, [navigate, previousCameraId]);

  const goNextCamera = useCallback(() => {
    if (nextCameraId != null) navigate(`/cameras/${nextCameraId}/playback`);
  }, [navigate, nextCameraId]);

  const selectedOffsetSeconds = useMemo(
    () => (selectedSegment && data ? getSegmentPlaybackOffsetSeconds(data.segments, selectedSegment.id) : 0),
    [data, selectedSegment],
  );

  useEffect(() => {
    if (!canRecordingView) {
      setSelectedSegment(null);
      setPlaybackWallClockMs(null);
      setExportRangeWallMs(null);
      setExportPickingActive(false);
      return;
    }
    if (!data) return;
    const dateChanged = previousTimelineDateRef.current !== date;
    previousTimelineDateRef.current = date;

    setSelectedSegment((current) => {
      if (current && data.segments.some((s) => s.id === current.id)) return current;
      if (dateChanged && date < todayIso && data.segments.length > 0) {
        return sortSegmentsChronological(data.segments)[0];
      }
      return null;
    });
  }, [canRecordingView, date, data, todayIso]);

  useEffect(() => {
    const id = window.setInterval(() => setHeaderTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setExportRangeWallMs(null);
    setExportPickingActive(false);
  }, [date]);

  useEffect(() => {
    lastSeededSegmentIdForPlaybackRef.current = null;
  }, [camera.id]);

  useEffect(() => {
    setManualRecording({
      active: camera.manualRecordingStartedAt != null,
      startedAt: camera.manualRecordingStartedAt,
    });
  }, [camera.id, camera.manualRecordingStartedAt]);

  /**
   * Poll backend for manual recording status so the button reflects external changes
   * (e.g. another tab/operator toggling, or backend reverting on MediaMTX failure).
   * Pauses while a local action is in-flight to avoid stomping on optimistic state.
   */
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const fetchStatus = async (): Promise<void> => {
      if (manualRecordingBusy) return;
      try {
        const status = await camerasApi.getManualRecording(camera.id, controller.signal);
        if (!cancelled) setManualRecording(status);
      } catch (err) {
        if (axios.isCancel(err) || controller.signal.aborted) return;
      }
    };

    const id = window.setInterval(() => {
      void fetchStatus();
    }, 10_000);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(id);
    };
  }, [camera.id, manualRecordingBusy]);

  const headerDateTimeLabel = useMemo(() => {
    void headerTick;
    const ms =
      !isLive && playbackWallClockMs !== null
        ? playbackWallClockMs
        : !isLive && selectedSegment
          ? new Date(selectedSegment.startTime).getTime()
          : Date.now();
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(new Date(ms));
  }, [isLive, playbackWallClockMs, selectedSegment, headerTick]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isLive || !data?.segments.length) {
      setPlaybackWallClockMs(null);
      return undefined;
    }
    if (phase !== 'playing') {
      return undefined;
    }

    const syncWallClock = (): void => {
      if (video.seeking || video.readyState < HTMLMediaElement.HAVE_METADATA) return;
      const dur =
        Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      const mappedOffset = offsetForWallClockMapping(data.segments, video.currentTime, dur);
      const ms = wallClockMsFromPlaylistOffset(data.segments, mappedOffset);
      if (ms !== null) {
        setPlaybackWallClockMs(ms);
      }
    };

    video.addEventListener('timeupdate', syncWallClock);
    video.addEventListener('seeked', syncWallClock);

    return () => {
      video.removeEventListener('timeupdate', syncWallClock);
      video.removeEventListener('seeked', syncWallClock);
    };
  }, [data, isLive, selectedSegment, phase]);

  /**
   * Seed header wall-clock only when the selected segment *identity* changes.
   * Do not re-run on timeline `data` refetches — stale `endTime` from metadata would snap playback
   * time back to segment start while the user is viewing a later instant.
   */
  useEffect(() => {
    if (isLive) {
      lastSeededSegmentIdForPlaybackRef.current = null;
      return;
    }
    if (!selectedSegment?.id || !data?.segments.length) return;
    const seg = data.segments.find((s) => s.id === selectedSegment.id);
    if (!seg) return;
    const id = selectedSegment.id;
    if (lastSeededSegmentIdForPlaybackRef.current === id) return;
    lastSeededSegmentIdForPlaybackRef.current = id;
    setPlaybackWallClockMs((prev) => {
      const ws = new Date(seg.startTime).getTime();
      const we = new Date(seg.endTime).getTime();
      const keep = prev !== null && prev >= ws && prev <= we;
      const next = keep ? prev : ws;
      return next;
    });
  }, [selectedSegment?.id, isLive, data]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const sync = (): void => {
      setVideoPlaying(!video.paused);
    };
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('emptied', sync);
    sync();
    return () => {
      video.removeEventListener('play', sync);
      video.removeEventListener('pause', sync);
      video.removeEventListener('emptied', sync);
    };
  }, [phase, isLive, date, selectedSegment?.id, camera.id]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isLive || phase !== 'playing') {
      setMediaBusy(false);
      return undefined;
    }
    const onBusy = (): void => {
      dbgPlayback('video:waiting');
      setMediaBusy(true);
    };
    const onIdle = (): void => {
      dbgPlayback('video:ready');
      setMediaBusy(false);
    };
    video.addEventListener('waiting', onBusy);
    video.addEventListener('playing', onIdle);
    video.addEventListener('canplay', onIdle);
    return () => {
      video.removeEventListener('waiting', onBusy);
      video.removeEventListener('playing', onIdle);
      video.removeEventListener('canplay', onIdle);
      setMediaBusy(false);
    };
  }, [isLive, phase, date, selectedSegment?.id, camera.id]);

  const handleTimelineSeek = useCallback(
    (wallMs: number) => {
      if (!data?.segments.length || isLive) return;
      const { offsetSeconds, segmentId } = playlistSeekFromWallClockMs(data.segments, wallMs);
      if (!segmentId) return;
      const seg = data.segments.find((s) => s.id === segmentId);
      if (!seg) return;

      setPlaybackWallClockMs(wallMs);

      if (selectedSegment?.id === segmentId) {
        const v = videoRef.current;
        if (v && phase === 'playing') {
          dbgPlayback('timeline:seek-same-segment', { segmentId, offsetSeconds });
          seekAndPlay(v, offsetSeconds, {
            seekCatchupRef: timelineSeekCatchupRef,
            catchupCancelled: () => playbackCancelledRef.current,
          });
        } else {
          pendingPlaylistSeek.current = offsetSeconds;
        }
        return;
      }

      pendingPlaylistSeek.current = offsetSeconds;
      setSelectedSegment(seg);
    },
    [data, isLive, selectedSegment?.id, phase],
  );

  const seekVideoBy = useCallback((deltaSeconds: number) => {
    if (isLive) return;
    const v = videoRef.current;
    if (!v || !data?.segments.length) return;
    const next = Math.max(0, Math.min(v.duration || Infinity, v.currentTime + deltaSeconds));
    seekAndPlay(v, next, {
      seekCatchupRef: timelineSeekCatchupRef,
      catchupCancelled: () => playbackCancelledRef.current,
    });
    const ms = wallClockMsFromPlaylistOffset(data.segments, next);
    if (ms !== null) setPlaybackWallClockMs(ms);
  }, [isLive, data?.segments]);

  const onPlayPause = useCallback(() => {
    if (isLive) return;
    const v = videoRef.current;
    if (!v || !data?.segments.length) return;
    if (v.paused) {
      void v.play().then(() => {
        const ms = wallClockMsFromPlaylistOffset(data.segments, v.currentTime);
        if (ms !== null) setPlaybackWallClockMs(ms);
      });
    } else {
      v.pause();
    }
  }, [isLive, data?.segments]);

  const onSnapshot = useCallback(async () => {
    if (!canExportSnapshot) return;
    if (isLive && camera.status === 'active') {
      try {
        const blob = await recordingsApi.liveSnapshotJpeg(camera.id);
        downloadBlob(blob, `snapshot-${camera.id}-${Date.now()}.jpg`);
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Snapshot failed';
        setExportHint(msg);
        window.setTimeout(() => setExportHint(null), 5000);
      }
      return;
    }

    const v = videoRef.current;
    if (!v || v.videoWidth === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try {
      ctx.drawImage(v, 0, 0);
    } catch {
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) return;
      downloadBlob(blob, `snapshot-${camera.id}-${Date.now()}.png`);
    }, 'image/png');
  }, [camera.id, camera.status, isLive, canExportSnapshot]);

  const onExportClip = useCallback(async () => {
    if (!canExportSnapshot) return;
    if (!data?.segments.length || exportBusy) return;

    if (!exportPickingActive) {
      setExportPickingActive(true);
      setExportHint(
        'Shift+drag on the timeline to choose start and end, then click Export again to download.',
      );
      window.setTimeout(() => setExportHint(null), 10000);
      return;
    }

    setExportHint(null);

    if (!exportRangeWallMs || exportRangeWallMs.end - exportRangeWallMs.start < 1000) {
      setExportHint(
        'Shift+drag on the timeline to select a range (at least ~2 seconds), then click Export again.',
      );
      window.setTimeout(() => setExportHint(null), 10000);
      return;
    }

    const startIso = new Date(exportRangeWallMs.start).toISOString();
    const endIso = new Date(exportRangeWallMs.end).toISOString();

    setExportBusy(true);
    try {
      const blob = await recordingsApi.exportClip({
        cameraId: camera.id,
        start: startIso,
        end: endIso,
        utcOffsetMinutes: getViewerUtcOffsetMinutes(),
      });
      downloadBlob(blob, `clip-${camera.id}-${Date.now()}.mp4`);
      setExportPickingActive(false);
      setExportRangeWallMs(null);
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Export failed';
      setExportHint(msg);
      window.setTimeout(() => setExportHint(null), 8000);
    } finally {
      setExportBusy(false);
    }
  }, [camera.id, data?.segments.length, exportBusy, exportPickingActive, exportRangeWallMs, canExportSnapshot]);

  const cancelExportPicking = useCallback(() => {
    setExportPickingActive(false);
    setExportRangeWallMs(null);
    setExportHint(null);
  }, []);

  const onToggleFullscreen = useCallback(() => {
    const el = frameRef.current;
    if (!el) return;
    if (!document.fullscreenElement) void el.requestFullscreen().catch(() => undefined);
    else void document.exitFullscreen();
  }, []);

  const onToggleManualRecording = useCallback(async () => {
    if (!canRecordingControl) return;
    if (manualRecordingBusy) return;
    if (!manualRecording.active && camera.status !== 'active') {
      setExportHint('Camera is offline; activate it before starting manual recording.');
      window.setTimeout(() => setExportHint(null), 6000);
      return;
    }
    setManualRecordingBusy(true);
    try {
      const next = manualRecording.active
        ? await camerasApi.stopManualRecording(camera.id)
        : await camerasApi.startManualRecording(camera.id);
      setManualRecording(next);
    } catch (err) {
      const msg =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : manualRecording.active
              ? 'Failed to stop manual recording'
              : 'Failed to start manual recording';
      setExportHint(msg);
      window.setTimeout(() => setExportHint(null), 6000);
    } finally {
      setManualRecordingBusy(false);
    }
  }, [camera.id, camera.status, manualRecording.active, manualRecordingBusy, canRecordingControl]);

  const manualRecordingDurationLabel = useMemo(() => {
    void headerTick;
    if (!manualRecording.active || !manualRecording.startedAt) return null;
    const startedMs = new Date(manualRecording.startedAt).getTime();
    if (Number.isNaN(startedMs)) return null;
    const elapsedSec = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
    const hh = Math.floor(elapsedSec / 3600);
    const mm = Math.floor((elapsedSec % 3600) / 60);
    const ss = elapsedSec % 60;
    const pad = (n: number): string => String(n).padStart(2, '0');
    return hh > 0 ? `${pad(hh)}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
  }, [manualRecording.active, manualRecording.startedAt, headerTick]);

  useEffect(() => {
    playbackCancelledRef.current = false;
    const video = videoRef.current;
    if (!video) return undefined;

    if (timelineSeekCatchupRef.current) {
      video.removeEventListener('durationchange', timelineSeekCatchupRef.current);
      timelineSeekCatchupRef.current = null;
    }

    const controller = new AbortController();
    let playbackSession: HlsPlaybackSession | null = null;
    let cancelled = false;

    setPhase('loading');
    setPlayerError(null);
    resetVideoElement(video);

    const onFatalPlaybackError = (message: string): void => {
      if (cancelled) return;
      setPlayerError(message);
      setPhase('error');
    };

    if (isLive) {
      if (camera.status !== 'active') {
        setPhase('offline');
        return () => controller.abort();
      }

      streamsApi
        .getDescriptor(camera.id, controller.signal)
        .then(async (descriptor) => {
          if (cancelled) return;
          if (descriptor.mediamtxStatus !== 'registered') {
            throw new Error(descriptor.mediamtxError ?? 'Stream not registered with MediaMTX');
          }

          playbackSession = await startHlsPlayback({
            video,
            sourceUrl: descriptor.hlsUrl,
            signal: controller.signal,
            lowLatencyMode: true,
            fatalErrorMessage: 'Failed to start live stream',
            onReady: () => {
              if (cancelled) return;
              void video.play().catch(() => undefined);
              setPhase('playing');
            },
            onFatalError: onFatalPlaybackError,
          });
        })
        .catch((err: unknown) => {
          if (cancelled || controller.signal.aborted) return;
          setPlayerError(toPlayerError(err, 'Failed to start live stream'));
          setPhase('error');
        });

      return () => {
        cancelled = true;
        controller.abort();
        playbackSession?.destroy();
        resetVideoElement(video);
      };
    }

    const playlistUrl = recordingsApi.getTimelinePlaylistUrl(camera.id, {
      date,
      utcOffsetMinutes: getViewerUtcOffsetMinutes(),
    });

    let started = false;
    const startTimelinePlayback = (): void => {
      if (cancelled || started) return;
      started = true;
      const seekTo =
        pendingPlaylistSeek.current !== null ? pendingPlaylistSeek.current : selectedOffsetSeconds;
      pendingPlaylistSeek.current = null;
      requestAnimationFrame(() => {
        if (cancelled) return;
        dbgPlayback('hls:start-playback', { seekTo });
        seekAndPlay(video, seekTo, {
          seekCatchupRef: timelineSeekCatchupRef,
          catchupCancelled: () => playbackCancelledRef.current,
        });
        setPhase('playing');
      });
    };

    void startHlsPlayback({
      video,
      sourceUrl: playlistUrl,
      signal: controller.signal,
      lowLatencyMode: false,
      fatalErrorMessage: 'Failed to load recording timeline',
      onReady: startTimelinePlayback,
      onFatalError: onFatalPlaybackError,
    })
      .then((session) => {
        if (cancelled) {
          session.destroy();
          return;
        }
        playbackSession = session;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPlayerError(toPlayerError(err, 'Failed to load playback engine'));
        setPhase('error');
      });

    return () => {
      cancelled = true;
      playbackCancelledRef.current = true;
      if (timelineSeekCatchupRef.current) {
        video.removeEventListener('durationchange', timelineSeekCatchupRef.current);
        timelineSeekCatchupRef.current = null;
      }
      controller.abort();
      playbackSession?.destroy();
      resetVideoElement(video);
    };
  }, [camera.id, camera.status, date, isLive, retryToken, selectedOffsetSeconds]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div
        ref={frameRef}
        className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-black shadow-card"
      >
        <video
          ref={videoRef}
          autoPlay
          muted={muted}
          playsInline
          className={clsx(
            'h-full w-full object-contain transition-opacity',
            !isLive && isLoading ? 'opacity-0' : phase === 'playing' ? 'opacity-100' : 'opacity-0',
          )}
        />

        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-black/75 via-black/25 to-transparent px-3 pb-8 pt-2">
          <div className="pointer-events-auto flex items-start justify-between gap-3">
            <span className="rounded-md bg-black/55 px-2.5 py-1 text-sm font-medium text-white backdrop-blur-sm">
              {camera.name}
            </span>
            <div className="flex items-center gap-3 text-sm tabular-nums text-accent">
              <span>{headerDateTimeLabel}</span>
              <button
                type="button"
                aria-label="More options"
                className="rounded-md p-1 text-white/80 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <MoreIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/75 via-black/20 to-transparent px-3 pt-10 pb-2">
          <div className="pointer-events-auto flex items-end justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-md bg-black/5 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
                <span
                  className={clsx('h-2 w-2 shrink-0 rounded-full', isLive ? 'bg-success' : 'bg-accent')}
                  aria-hidden
                />
                {isLive ? 'Live' : 'Playback'}
              </div>
              {isLive && manualRecording.active ? (
                <div
                  role="status"
                  aria-label={`Manual recording active${
                    manualRecordingDurationLabel ? ` (${manualRecordingDurationLabel})` : ''
                  }`}
                  className="flex items-center gap-1.5 rounded-md bg-danger/85 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm"
                >
                  <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-white" aria-hidden />
                  REC
                  {manualRecordingDurationLabel ? (
                    <span className="tabular-nums">{manualRecordingDurationLabel}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="flex flex-col items-end gap-1">
              {exportHint ? (
                <span className="max-w-[14rem] rounded-md bg-black/70 px-2 py-1 text-[11px] leading-snug text-white">
                  {exportHint}
                </span>
              ) : null}
              <div className="flex items-center gap-2">
              {isLive && canRecordingControl ? (
                <RoundVideoAction
                  label={
                    manualRecordingBusy
                      ? manualRecording.active
                        ? 'Stopping manual recording…'
                        : 'Starting manual recording…'
                      : manualRecording.active
                        ? `Stop manual recording${
                            manualRecordingDurationLabel ? ` (${manualRecordingDurationLabel})` : ''
                          }`
                        : camera.status !== 'active'
                          ? 'Camera is offline'
                          : 'Start manual recording'
                  }
                  disabled={manualRecordingBusy || (!manualRecording.active && camera.status !== 'active')}
                  active={manualRecording.active}
                  onClick={() => void onToggleManualRecording()}
                >
                  <ManualRecordIcon busy={manualRecordingBusy} active={manualRecording.active} />
                </RoundVideoAction>
              ) : null}
              <RoundVideoAction
                label={
                  isLive
                    ? 'Save snapshot (camera stream)'
                    : 'Save snapshot from video frame'
                }
                disabled={!canExportSnapshot}
                onClick={() => void onSnapshot()}
              >
                <SnapshotIcon />
              </RoundVideoAction>
              {canRecordingView ? (
                <RoundVideoAction
                  label={
                    exportBusy
                      ? 'Exporting clip…'
                      : !exportPickingActive
                        ? 'Export MP4 — click to choose a time range on the timeline'
                        : !exportRangeWallMs || exportRangeWallMs.end - exportRangeWallMs.start < 1000
                          ? 'Export MP4 — Shift+drag on the timeline, then click again to download'
                          : 'Export MP4 — click again to download this range'
                  }
                  disabled={!canExportSnapshot || !data?.segments.length || exportBusy}
                  onClick={() => void onExportClip()}
                >
                  <ExportClipIcon busy={exportBusy} />
                </RoundVideoAction>
              ) : null}
              <RoundVideoAction
                label={muted ? 'Unmute' : 'Mute'}
                onClick={() => setMuted((m) => !m)}
              >
                {muted ? <VolumeMutedIcon /> : <VolumeIcon />}
              </RoundVideoAction>
              <RoundVideoAction label="Fullscreen" onClick={onToggleFullscreen}>
                <FullscreenIcon />
              </RoundVideoAction>
              </div>
            </div>
          </div>
        </div>

        {showPlaybackOverlay ? (
          <div className="absolute inset-0 z-[5] flex items-center justify-center bg-bg/50 text-center backdrop-blur-[2px]">
            <div className="flex max-w-sm flex-col items-center gap-3 px-4">
              {phase === 'error' ? (
                <>
                  <span className="text-xs font-medium text-danger">
                    {isLive ? 'Stream unavailable' : 'Playback unavailable'}
                  </span>
                  {playerError ? (
                    <span className="line-clamp-2 text-xs text-muted">{playerError}</span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      refresh();
                      setRetryToken((value) => value + 1);
                    }}
                    className={clsx(
                      'pointer-events-auto rounded border border-border bg-surface px-2 py-0.5 text-[11px] text-foreground',
                      'hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    )}
                  >
                    Retry
                  </button>
                </>
              ) : null}
              {phase === 'offline' ? (
                <span className="text-xs uppercase tracking-wider text-muted">Camera offline</span>
              ) : null}
              {phase !== 'error' &&
              phase !== 'offline' &&
              (phase === 'loading' || (!isLive && isLoading) || (!isLive && phase === 'playing' && mediaBusy)) ? (
                <>
                  <PlaybackSpinner className="h-9 w-9 text-accent" />
                  <span className="text-xs uppercase tracking-wider text-muted">
                    {isLive
                      ? 'Connecting…'
                      : isLoading
                        ? 'Loading timeline…'
                        : phase === 'playing' && mediaBusy
                          ? 'Buffering…'
                          : 'Preparing playback…'}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {canRecordingView ? (
        <PlaybackTimeline
          timeline={data}
          selectedSegmentId={selectedSegment?.id}
          isLoading={isLoading}
          busyMessage={timelineBusyMessage}
          error={error}
          onSelectSegment={setSelectedSegment}
          onRefresh={refresh}
          isLive={isLive}
          playbackWallClockMs={playbackWallClockMs}
          selectedDate={date}
          onDateChange={setDate}
          showJumpToToday={showJumpToToday}
          onJumpToToday={() => setDate(todayIso)}
          isPlaying={videoPlaying}
          playPauseDisabled={isLive}
          onPlayPause={onPlayPause}
          onPreviousCamera={goPreviousCamera}
          onNextCamera={goNextCamera}
          canPreviousCamera={previousCameraId != null}
          canNextCamera={nextCameraId != null}
          seekStepSeconds={SEEK_STEP_SECONDS}
          seekDisabled={isLive}
          onRewind={() => seekVideoBy(-SEEK_STEP_SECONDS)}
          onFastForward={() => seekVideoBy(SEEK_STEP_SECONDS)}
          canGoLive={!isLive}
          onGoLive={() => setSelectedSegment(null)}
          timelineInteractive={Boolean(data?.segments.length) && !timelineBusyMessage}
          onTimelineSeek={handleTimelineSeek}
          exportSelectionActive={exportPickingActive}
          onCancelExportSelection={cancelExportPicking}
          exportRangeWallMs={exportRangeWallMs}
          onExportRangeWallMsChange={setExportRangeWallMs}
        />
      ) : (
        <div className="rounded-lg border border-border bg-surface px-3 py-2 text-xs text-muted">
          Recording timeline is hidden for your role.
        </div>
      )}
    </div>
  );
}

function PlaybackSpinner({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={clsx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-30" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path
        d="M21 12a9 9 0 00-9-9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RoundVideoAction({
  label,
  onClick,
  disabled,
  active,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active ? true : undefined}
      title={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onClick();
      }}
      className={clsx(
        'flex h-9 w-9 items-center justify-center rounded-full border text-white backdrop-blur-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50',
        active
          ? 'border-danger/70 bg-danger/80 hover:bg-danger'
          : 'border-white/15 bg-black/45 hover:bg-black/65',
        disabled && !active && 'cursor-not-allowed opacity-40 hover:bg-black/45',
        disabled && active && 'cursor-not-allowed opacity-70',
      )}
    >
      {children}
    </button>
  );
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ExportClipIcon({ busy }: { busy: boolean }): JSX.Element {
  if (busy) {
    return (
      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle
          className="opacity-30"
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M21 12a9 9 0 00-9-9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 3v12" strokeLinecap="round" />
      <path d="M8 11l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 19h14" strokeLinecap="round" />
    </svg>
  );
}

function ManualRecordIcon({ busy, active }: { busy: boolean; active: boolean }): JSX.Element {
  if (busy) {
    return (
      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle
          className="opacity-30"
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M21 12a9 9 0 00-9-9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (active) {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
        <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="5" fill="currentColor" />
    </svg>
  );
}

function SnapshotIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7h4l2-2h4l2 2h4v12H4V7z" strokeLinejoin="round" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

function VolumeIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11 5L6 9H3v6h3l5 4V5zm7.5 3.5a5 5 0 010 7M17 9a3 3 0 010 6" />
    </svg>
  );
}

function VolumeMutedIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11 5L6 9H3v6h3l5 4V5z" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        d="M17 9l6 6M23 9l-6 6"
      />
    </svg>
  );
}

function FullscreenIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MoreIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  );
}

function detachTimelineSeekCatchup(
  video: HTMLVideoElement,
  ref: MutableRefObject<(() => void) | null>,
): void {
  if (ref.current) {
    video.removeEventListener('durationchange', ref.current);
    ref.current = null;
  }
}

type SeekAndPlayOpts = {
  seekCatchupRef?: MutableRefObject<(() => void) | null>;
  catchupCancelled?: () => boolean;
};

function registerDurationSeekCatchup(
  video: HTMLVideoElement,
  want: number,
  ref: MutableRefObject<(() => void) | null>,
  cancelled: () => boolean,
): void {
  detachTimelineSeekCatchup(video, ref);
  if (want <= 0) return;

  let fires = 0;
  const handler = (): void => {
    if (cancelled() || ++fires > 48) {
      detachTimelineSeekCatchup(video, ref);
      return;
    }
    const d = video.duration;
    if (!Number.isFinite(d) || d <= 0) return;
    if (want > d - 0.05) return;
    if (Math.abs(video.currentTime - want) <= 1.5) {
      detachTimelineSeekCatchup(video, ref);
      return;
    }
    detachTimelineSeekCatchup(video, ref);
    seekAndPlay(video, want, {
      seekCatchupRef: ref,
      catchupCancelled: cancelled,
    });
  };
  ref.current = handler;
  video.addEventListener('durationchange', handler);
}

function seekAndPlay(video: HTMLVideoElement, offsetSeconds: number, opts?: SeekAndPlayOpts): void {
  const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
  const target = resolveTimelineSeekTargetSeconds(offsetSeconds, duration);
  dbgPlayback('seekAndPlay', { offsetSeconds, target, duration });
  const cancelled = opts?.catchupCancelled ?? (() => false);
  if (opts?.seekCatchupRef) {
    if (offsetSeconds > 0 && (duration == null || offsetSeconds > duration - 0.15)) {
      registerDurationSeekCatchup(video, offsetSeconds, opts.seekCatchupRef, cancelled);
    } else {
      detachTimelineSeekCatchup(video, opts.seekCatchupRef);
    }
  }
  video.currentTime = target;
  dbgPlayback('seekAndPlay:play', { target });
  void video.play().catch(() => {
    requestAnimationFrame(() => {
      void video.play().catch(() => undefined);
    });
  });
}

function toPlayerError(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}
